import { PayloadAction } from '@reduxjs/toolkit';
import { DateTime } from 'luxon';
import { SystemMinimumDate } from 'src/common';
import { DatePickerNames } from 'src/common/components/DateRangePickerPreDefinedDates';
import { initialState } from './slice';
import {
  ClientNewReportsState,
  CreateInitiateReportRequest,
  InitiateReportRequest,
  MdaReportType,
  MdaReportTypes,
  ReportFormat,
  ReportToSort,
  SelectedReport,
  YearType,
} from './types';

const dateTo = DateTime.now().toISODate();

const getDateRangeType = (dateRangeType: string | null | undefined, isDateOnly: boolean) => {
  const defaultText = `Pre Defined ${isDateOnly ? 'Date' : 'Dates'}`;
  if (dateRangeType === null || dateRangeType === undefined || dateRangeType === '') return defaultText;
  return dateRangeType;
};

export function getReportsToSort(reportsToSort: ReportToSort[], format?: string): ReportToSort[] {
  if (format === ReportFormat.Excel) {
    return reportsToSort.filter(
      (report) =>
        report.id === MdaReportTypes.AssetClassPerformance.id ||
        report.id === MdaReportTypes.AssetAllocation.id ||
        report.id === MdaReportTypes.CashTransactions.id ||
        report.id === MdaReportTypes.ContributionsAndWithdrawals.id ||
        report.id === MdaReportTypes.Expenses.id ||
        report.id === MdaReportTypes.HoldingsVsTargetVariance.id ||
        report.id === MdaReportTypes.IncomeReport.id ||
        report.id === MdaReportTypes.InvestmentIncomeTax.id ||
        report.id === MdaReportTypes.PortfolioPerformance.id ||
        report.id === MdaReportTypes.PortfolioSnapshot.id ||
        report.id === MdaReportTypes.PortfolioValuation.id ||
        report.id === MdaReportTypes.RealisedCapitalGainsAndLosses.id ||
        report.id === MdaReportTypes.SecurityContribution.id ||
        report.id === MdaReportTypes.SecurityPerformance.id ||
        report.id === MdaReportTypes.TransactionHistory.id ||
        report.id === MdaReportTypes.UnrealisedCapitalGainsAndLosses.id ||
        report.id === MdaReportTypes.UnrealisedNonCgt.id
    );
  }

  return reportsToSort;
}

export const createInitiateReportRequest = ({ isDataFromTriumph, clientId, parameters, handleClickMergeReports }: CreateInitiateReportRequest) => {
  let reportName = '';

  function getReportRequest<T>(type: SelectedReport<T>, name: string): Omit<SelectedReport<T>, 'expanded' | 'selected'> | undefined {
    if (!type.selected) {
      return undefined;
    }
    reportName = name;
    const { ...rest } = type;
    delete rest.expanded;
    delete rest.selected;
    return rest;
  }

  const payload: InitiateReportRequest = {
    isDataFromTriumph: isDataFromTriumph,
    clientId: clientId ?? -1,
    fromDate: parameters.dates.dateFrom,
    toDate: parameters.dates.dateTo,
    dateRangeType: getDateRangeType(parameters.dates.dateRangeType, false),
    includeLogo: parameters.includeLogo,
    mergeReportOrder: [],
    contributionsAndWithdrawals: getReportRequest(parameters.selectedReports.contributionsAndWithdrawals, MdaReportType.ContributionsAndWithdrawals),
    assetClassPerformance: getReportRequest(parameters.selectedReports.assetClassPerformance, MdaReportType.AssetClassPerformance),
    holdingsVsTargetVariance: getReportRequest(parameters.selectedReports.holdingsVsTargetVariance, MdaReportType.HoldingsVsTargetVariance),
    cashTransactions: getReportRequest(parameters.selectedReports.cashTransactions, MdaReportType.CashTransactions),
    unrealisedCapitalGainsAndLosses: getReportRequest(
      parameters.selectedReports.unrealisedCapitalGainsAndLosses,
      MdaReportType.UnrealisedCapitalGainsAndLosses
    ),
    realisedCapitalGainsAndLosses: getReportRequest(
      parameters.selectedReports.realisedCapitalGainsAndLosses,
      MdaReportType.RealisedCapitalGainsAndLosses
    ),
    securityPerformance: getReportRequest(parameters.selectedReports.securityPerformance, MdaReportType.SecurityPerformance),
    expenses: getReportRequest(parameters.selectedReports.expenses, MdaReportType.Expenses),
    incomeReport: getReportRequest(parameters.selectedReports.incomeReport, MdaReportType.IncomeReport),
    portfolioSnapshot: getReportRequest(parameters.selectedReports.portfolioSnapshot, MdaReportType.PortfolioSnapshot),
    portfolioValuation: getReportRequest(parameters.selectedReports.portfolioValuation, MdaReportType.PortfolioValuation),
    portfolioPerformance: getReportRequest(parameters.selectedReports.portfolioPerformance, MdaReportType.PortfolioPerformance),
    projectedIncomeCashflow: getReportRequest(parameters.selectedReports.projectedIncomeCashflow, MdaReportType.ProjectedIncomeCashflow),
    securityContribution: getReportRequest(parameters.selectedReports.securityContribution, MdaReportType.SecurityContribution),
    assetAllocation: getReportRequest(parameters.selectedReports.assetAllocation, MdaReportType.AssetAllocation),
    taxSummary:
      parameters.reportFormat === ReportFormat.Excel ? undefined : getReportRequest(parameters.selectedReports.taxSummary, MdaReportType.TaxSummary),
    taxInvoice:
      parameters.reportFormat === ReportFormat.Excel ? undefined : getReportRequest(parameters.selectedReports.taxInvoice, MdaReportType.TaxInvoice),
    unrealisedNonCGT: getReportRequest(parameters.selectedReports.unrealisedNonCGT, MdaReportType.UnrealisedNonCGT),
    transactionHistory: getReportRequest(parameters.selectedReports.transactionHistory, MdaReportType.TransactionHistory),
    investmentIncomeTaxComponent: getReportRequest(parameters.selectedReports.investmentIncomeTaxComponent, MdaReportType.InvestmentIncomeTax),
    uXchangeTaxStatement: getReportRequest(parameters.selectedReports.uXchangeTaxStatement, MdaReportType.UXChangeTaxStatement),
    currencyExposure:
      parameters.reportFormat === ReportFormat.Excel
        ? undefined
        : getReportRequest(parameters.selectedReports.currencyExposure, MdaReportType.CurrencyExposure),
    includeCoverLetter: parameters.includeCoverLetter,
    includeAppendix: parameters.includeAppendix,
    coverLetter: parameters.coverLetter,
    appendix: parameters.appendix,
    reportFormat: parameters.reportFormat,
  };
  const reportsToSortIds = getReportsToSort(parameters.reportsToSort, parameters.reportFormat).map((report) => report.id);
  if (parameters.mergeReports) {
    if (parameters.reportsToSort.length > 1 || parameters.includeAppendix || parameters.includeCoverLetter) {
      // If there are more than 1 report, or if the cover letter or appendix is included, we need to ensure that the merge report is always first and the consolidated disclaimer is always last.
      // This is because the merge report will handle merging the reports and the consolidated disclaimer needs to be last to ensure it is included in the merge report as the last page.
      // In the future, if we want to allow users to generate merge reports without disclaimer, we can add a toggle for the disclaimer and only include it in the merge report order only if the toggle is on
      payload.mergeReportOrder = [MdaReportTypes.MergeReport.id, ...reportsToSortIds];
    } else {
      payload.mergeReportOrder = reportsToSortIds;
      handleClickMergeReports();
    }
  } else {
    payload.mergeReportOrder = reportsToSortIds;
  }

  return { reportName: reportName, reportRequest: payload };
};

export const updateReportParameters = (state: ClientNewReportsState, action: PayloadAction<InitiateReportRequest>) => {
  const dateFrom = state.parameters.inceptionDate.length > 0 ? state.parameters.inceptionDate : SystemMinimumDate;
  state.parameters.dates = {
    dateFrom: action.payload.fromDate,
    dateTo: action.payload.toDate,
    dateRangeType: action.payload.dateRangeType,
  };

  state.parameters.selectedReports = { ...initialState.parameters.selectedReports };
  state.parameters.includeAppendix = action.payload.includeAppendix;
  state.parameters.includeCoverLetter = action.payload.includeCoverLetter;
  state.parameters.includeLogo = action.payload.includeLogo;
  state.parameters.appendix = action.payload.appendix;
  state.parameters.coverLetter = action.payload.coverLetter;
  state.parameters.reportFormat = action.payload.reportFormat;
  state.parameters.parameterName = action.payload.parameterName;
  state.parameters.mergeReports = action.payload.mergeReportOrder.includes(MdaReportTypes.MergeReport.id);
  state.parameters.isDataFromTriumph = action.payload.isDataFromTriumph;
  state.parameters.reportsToSort = [];

  if (action.payload.contributionsAndWithdrawals) {
    const report = action.payload.contributionsAndWithdrawals;
    state.parameters.selectedReports.contributionsAndWithdrawals = {
      selected: true,
      expanded: false,
      ...report,
      fromDate: report.dateRangeType === DatePickerNames.InceptionToDate ? dateFrom : report.fromDate,
    };
    state.parameters.reportsToSort.push({
      id: MdaReportTypes.ContributionsAndWithdrawals.id,
      text: MdaReportTypes.ContributionsAndWithdrawals.displayName,
    });
  } else {
    state.parameters.selectedReports.contributionsAndWithdrawals = {
      ...initialState.parameters.selectedReports.contributionsAndWithdrawals,
      fromDate: dateFrom,
      toDate: dateTo,
    };
  }
  if (action.payload.projectedIncomeCashflow) {
    const report = action.payload.projectedIncomeCashflow;
    state.parameters.selectedReports.projectedIncomeCashflow = {
      selected: true,
      expanded: false,
      ...report,
    };
    state.parameters.reportsToSort.push({
      id: MdaReportTypes.ProjectedIncomeCashflow.id,
      text: MdaReportTypes.ProjectedIncomeCashflow.displayName,
    });
  } else {
    state.parameters.selectedReports.projectedIncomeCashflow = {
      ...initialState.parameters.selectedReports.projectedIncomeCashflow,
      asAtDate: dateTo,
    };
  }
  if (action.payload.assetAllocation) {
    const report = action.payload.assetAllocation;
    state.parameters.selectedReports.assetAllocation = {
      selected: true,
      expanded: false,
      ...report,
    };
    state.parameters.reportsToSort.push({
      id: MdaReportTypes.AssetAllocation.id,
      text: MdaReportTypes.AssetAllocation.displayName,
    });
  } else {
    state.parameters.selectedReports.assetAllocation = {
      ...initialState.parameters.selectedReports.assetAllocation,
      asAtDate: dateTo,
    };
  }
  if (action.payload.assetClassPerformance) {
    const report = action.payload.assetClassPerformance;
    state.parameters.selectedReports.assetClassPerformance = {
      selected: true,
      expanded: false,
      ...report,
      fromDate: report.dateRangeType === DatePickerNames.InceptionToDate ? dateFrom : report.fromDate,
    };
    state.parameters.reportsToSort.push({
      id: MdaReportTypes.AssetClassPerformance.id,
      text: MdaReportTypes.AssetClassPerformance.displayName,
    });
  } else {
    state.parameters.selectedReports.assetClassPerformance = {
      ...initialState.parameters.selectedReports.assetClassPerformance,
      fromDate: dateFrom,
      toDate: dateTo,
    };
  }
  if (action.payload.currencyExposure) {
    const report = action.payload.currencyExposure;
    state.parameters.selectedReports.currencyExposure = {
      selected: true,
      expanded: false,
      ...report,
    };
    state.parameters.reportsToSort.push({
      id: MdaReportTypes.CurrencyExposure.id,
      text: MdaReportTypes.CurrencyExposure.displayName,
    });
  } else {
    state.parameters.selectedReports.currencyExposure = {
      ...initialState.parameters.selectedReports.currencyExposure,
      asAtDate: dateTo,
    };
  }
  if (action.payload.holdingsVsTargetVariance) {
    const report = action.payload.holdingsVsTargetVariance;
    state.parameters.selectedReports.holdingsVsTargetVariance = {
      selected: true,
      expanded: false,
      ...report,
    };
    state.parameters.reportsToSort.push({
      id: MdaReportTypes.HoldingsVsTargetVariance.id,
      text: MdaReportTypes.HoldingsVsTargetVariance.displayName,
    });
  } else {
    state.parameters.selectedReports.holdingsVsTargetVariance = {
      ...initialState.parameters.selectedReports.holdingsVsTargetVariance,
      asAtDate: dateTo,
    };
  }
  if (action.payload.cashTransactions) {
    const report = action.payload.cashTransactions;
    state.parameters.selectedReports.cashTransactions = {
      selected: true,
      expanded: false,
      ...report,
      fromDate: report.dateRangeType === DatePickerNames.InceptionToDate ? dateFrom : report.fromDate,
    };
    state.parameters.reportsToSort.push({
      id: MdaReportTypes.CashTransactions.id,
      text: MdaReportTypes.CashTransactions.displayName,
    });
  } else {
    state.parameters.selectedReports.cashTransactions = {
      ...initialState.parameters.selectedReports.cashTransactions,
      fromDate: dateFrom,
      toDate: dateTo,
    };
  }
  if (action.payload.expenses) {
    const report = action.payload.expenses;
    state.parameters.selectedReports.expenses = {
      selected: true,
      expanded: false,
      ...report,
      fromDate: report.dateRangeType === DatePickerNames.InceptionToDate ? dateFrom : report.fromDate,
    };
    state.parameters.reportsToSort.push({
      id: MdaReportTypes.Expenses.id,
      text: MdaReportTypes.Expenses.displayName,
    });
  } else {
    state.parameters.selectedReports.expenses = {
      ...initialState.parameters.selectedReports.expenses,
      fromDate: dateFrom,
      toDate: dateTo,
    };
  }
  if (action.payload.incomeReport) {
    const report = action.payload.incomeReport;
    state.parameters.selectedReports.incomeReport = {
      selected: true,
      expanded: false,
      ...report,
      fromDate: report.dateRangeType === DatePickerNames.InceptionToDate ? dateFrom : report.fromDate,
    };
    state.parameters.reportsToSort.push({
      id: MdaReportTypes.IncomeReport.id,
      text: MdaReportTypes.IncomeReport.displayName,
    });
  } else {
    state.parameters.selectedReports.incomeReport = {
      ...initialState.parameters.selectedReports.incomeReport,
      fromDate: dateFrom,
      toDate: dateTo,
    };
  }
  if (action.payload.securityPerformance) {
    const report = action.payload.securityPerformance;
    state.parameters.selectedReports.securityPerformance = {
      selected: true,
      expanded: false,
      ...report,
      fromDate: report.dateRangeType === DatePickerNames.InceptionToDate ? dateFrom : report.fromDate,
    };
    state.parameters.reportsToSort.push({
      id: MdaReportTypes.SecurityPerformance.id,
      text: MdaReportTypes.SecurityPerformance.displayName,
    });
  } else {
    state.parameters.selectedReports.securityPerformance = {
      ...initialState.parameters.selectedReports.securityPerformance,
      fromDate: dateFrom,
      toDate: dateTo,
    };
  }
  if (action.payload.portfolioSnapshot) {
    const report = action.payload.portfolioSnapshot;
    state.parameters.selectedReports.portfolioSnapshot = {
      selected: true,
      expanded: false,
      ...report,
      fromDate: report.dateRangeType === DatePickerNames.InceptionToDate ? dateFrom : report.fromDate,
    };
    state.parameters.reportsToSort.push({
      id: MdaReportTypes.PortfolioSnapshot.id,
      text: MdaReportTypes.PortfolioSnapshot.displayName,
    });
  } else {
    state.parameters.selectedReports.portfolioSnapshot = {
      ...initialState.parameters.selectedReports.portfolioSnapshot,
      fromDate: dateFrom,
      toDate: dateTo,
    };
  }
  if (action.payload.taxInvoice) {
    const report = action.payload.taxInvoice;
    state.parameters.selectedReports.taxInvoice = {
      selected: true,
      expanded: false,
      ...report,
      fromDate: report.dateRangeType === DatePickerNames.InceptionToDate ? dateFrom : report.fromDate,
    };
    state.parameters.reportsToSort.push({
      id: MdaReportTypes.TaxInvoice.id,
      text: MdaReportTypes.TaxInvoice.displayName,
    });
  } else {
    state.parameters.selectedReports.taxInvoice = {
      ...initialState.parameters.selectedReports.taxInvoice,
      fromDate: dateFrom,
      toDate: dateTo,
    };
  }
  if (action.payload.portfolioPerformance) {
    const report = action.payload.portfolioPerformance;
    state.parameters.selectedReports.portfolioPerformance = {
      selected: true,
      expanded: false,
      ...report,
      fromDate: report.dateRangeType === DatePickerNames.InceptionToDate ? dateFrom : report.fromDate,
    };
    state.parameters.reportsToSort.push({
      id: MdaReportTypes.PortfolioPerformance.id,
      text: MdaReportTypes.PortfolioPerformance.displayName,
    });
  } else {
    state.parameters.selectedReports.portfolioPerformance = {
      ...initialState.parameters.selectedReports.portfolioPerformance,
      fromDate: dateFrom,
      toDate: dateTo,
    };
  }
  if (action.payload.portfolioValuation) {
    const report = action.payload.portfolioValuation;
    state.parameters.selectedReports.portfolioValuation = {
      selected: true,
      expanded: false,
      ...report,
    };
    state.parameters.reportsToSort.push({
      id: MdaReportTypes.PortfolioValuation.id,
      text: MdaReportTypes.PortfolioValuation.displayName,
    });
  } else {
    state.parameters.selectedReports.portfolioValuation = {
      ...initialState.parameters.selectedReports.portfolioValuation,
      asAtDate: dateTo,
    };
  }
  if (action.payload.realisedCapitalGainsAndLosses) {
    const report = action.payload.realisedCapitalGainsAndLosses;
    state.parameters.selectedReports.realisedCapitalGainsAndLosses = {
      selected: true,
      expanded: false,
      ...report,
      fromDate: report.dateRangeType === DatePickerNames.InceptionToDate ? dateFrom : report.fromDate,
    };
    state.parameters.reportsToSort.push({
      id: MdaReportTypes.RealisedCapitalGainsAndLosses.id,
      text: MdaReportTypes.RealisedCapitalGainsAndLosses.displayName,
    });
  } else {
    state.parameters.selectedReports.realisedCapitalGainsAndLosses = {
      ...initialState.parameters.selectedReports.realisedCapitalGainsAndLosses,
      fromDate: dateFrom,
      toDate: dateTo,
    };
  }
  if (action.payload.unrealisedCapitalGainsAndLosses) {
    const report = action.payload.unrealisedCapitalGainsAndLosses;
    state.parameters.selectedReports.unrealisedCapitalGainsAndLosses = {
      selected: true,
      expanded: false,
      ...report,
    };
    state.parameters.reportsToSort.push({
      id: MdaReportTypes.UnrealisedCapitalGainsAndLosses.id,
      text: MdaReportTypes.UnrealisedCapitalGainsAndLosses.displayName,
    });
  } else {
    state.parameters.selectedReports.unrealisedCapitalGainsAndLosses = {
      ...initialState.parameters.selectedReports.unrealisedCapitalGainsAndLosses,
      asAtDate: dateTo,
    };
  }
  if (action.payload.securityContribution) {
    const report = action.payload.securityContribution;
    state.parameters.selectedReports.securityContribution = {
      selected: true,
      expanded: false,
      ...report,
      fromDate: report.dateRangeType === DatePickerNames.InceptionToDate ? dateFrom : report.fromDate,
    };
    state.parameters.reportsToSort.push({
      id: MdaReportTypes.SecurityContribution.id,
      text: MdaReportTypes.SecurityContribution.displayName,
    });
  } else {
    state.parameters.selectedReports.securityContribution = {
      ...initialState.parameters.selectedReports.securityContribution,
      fromDate: dateFrom,
      toDate: dateTo,
    };
  }
  if (action.payload.taxSummary) {
    const report = action.payload.taxSummary;
    state.parameters.selectedReports.taxSummary = {
      selected: true,
      expanded: false,
      ...report,
      fromDate: report.dateRangeType === DatePickerNames.InceptionToDate ? dateFrom : report.fromDate,
      yearType: report.yearType ?? YearType.FinancialYear,
    };
    state.parameters.reportsToSort.push({
      id: MdaReportTypes.TaxSummary.id,
      text: MdaReportTypes.TaxSummary.displayName,
    });
  } else {
    const currentDate = DateTime.now();
    state.parameters.selectedReports.taxSummary = {
      ...initialState.parameters.selectedReports.taxSummary,
      fromDate: DateTime.fromObject({ year: DateTime.now().year - 1, month: 7, day: 1 }).toISODate(),
      toDate: DateTime.fromObject({ year: currentDate.year, month: 6, day: 30 }).toISODate(),
    };
  }
  if (action.payload.investmentIncomeTaxComponent) {
    const report = action.payload.investmentIncomeTaxComponent;
    state.parameters.selectedReports.investmentIncomeTaxComponent = {
      selected: true,
      expanded: false,
      ...report,
      yearType: report.yearType ?? YearType.FinancialYear,
      version: report.version ?? 'Formatted',
    };
    state.parameters.reportsToSort.push({
      id: MdaReportTypes.InvestmentIncomeTax.id,
      text: MdaReportTypes.InvestmentIncomeTax.displayName,
    });
  } else {
    const currentDate = DateTime.now();
    state.parameters.selectedReports.investmentIncomeTaxComponent = {
      ...initialState.parameters.selectedReports.investmentIncomeTaxComponent,
      fromDate: DateTime.fromObject({ year: DateTime.now().year - 1, month: 7, day: 1 }).toISODate(),
      toDate: DateTime.fromObject({ year: currentDate.year, month: 6, day: 30 }).toISODate(),
      yearType: YearType.FinancialYear,
      version: 'Formatted',
    };
  }
  if (action.payload.transactionHistory) {
    const report = action.payload.transactionHistory;
    state.parameters.selectedReports.transactionHistory = {
      selected: true,
      expanded: false,
      ...report,
      fromDate: report.dateRangeType === DatePickerNames.InceptionToDate ? dateFrom : report.fromDate,
    };
    state.parameters.reportsToSort.push({
      id: MdaReportTypes.TransactionHistory.id,
      text: MdaReportTypes.TransactionHistory.displayName,
    });
  } else {
    state.parameters.selectedReports.transactionHistory = {
      ...initialState.parameters.selectedReports.transactionHistory,
      fromDate: dateFrom,
      toDate: dateTo,
    };
  }
  if (action.payload.unrealisedNonCGT) {
    const report = action.payload.unrealisedNonCGT;
    state.parameters.selectedReports.unrealisedNonCGT = {
      selected: true,
      expanded: false,
      ...report,
    };
    state.parameters.reportsToSort.push({
      id: MdaReportTypes.UnrealisedNonCgt.id,
      text: MdaReportTypes.UnrealisedNonCgt.displayName,
    });
  } else {
    state.parameters.selectedReports.unrealisedNonCGT = {
      ...initialState.parameters.selectedReports.unrealisedNonCGT,
      asAtDate: dateTo,
    };
  }
  if (action.payload.uXchangeTaxStatement) {
    const report = action.payload.uXchangeTaxStatement;
    state.parameters.selectedReports.uXchangeTaxStatement = {
      selected: true,
      expanded: false,
      ...report,
    };
    state.parameters.reportsToSort.push({
      id: MdaReportTypes.UXChangeTaxStatement.id,
      text: MdaReportTypes.UXChangeTaxStatement.displayName,
    });
  } else {
    const currentDate = DateTime.now();
    state.parameters.selectedReports.uXchangeTaxStatement = {
      ...initialState.parameters.selectedReports.uXchangeTaxStatement,
      fromDate: DateTime.fromObject({ year: DateTime.now().year - 1, month: 7, day: 1 }).toISODate(),
      toDate: DateTime.fromObject({ year: currentDate.year, month: 6, day: 30 }).toISODate(),
    };
  }
  state.parameters.includeLogo = action.payload.includeLogo;
  state.parameters.reportsToSort = state.parameters.reportsToSort.sort(
    (a, b) => action.payload.mergeReportOrder.indexOf(a.id) - action.payload.mergeReportOrder.indexOf(b.id)
  );
};
